import React, { useState } from 'react';
import {
  DataGrid,
  GridColDef,
  GridFooterContainer,
  GridPagination,
  itIT,
  useGridApiContext,
  useGridSelector,
  gridPaginationModelSelector,
  GridPaginationModel,
  gridFilteredSortedRowIdsSelector,
  useGridApiRef,
} from '@mui/x-data-grid';
import {
  Box,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Stack,
  Typography,
  Tooltip,
  Chip,
  ListItemIcon,
  ListItemText,
  Menu,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import IconButton from '@mui/material/IconButton';

import { Team, User } from '@tassomutuo-backoffice/types';
import {
  AutocompleteSelectInput,
  CheckboxInput,
} from '@tassomutuo-backoffice/ui';
import { useDateFormat } from '../../../../hooks';
import TeamMemberEditSpecificCapModal from './TeamMemberEditSpecificCapModal';
import TeamMemberEditTelephoneModal from './TeamMemberEditTelephoneModal';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TeamUserBqIntegrityStatus from './TeamUserBqIntegrityStatus';
import { exportUsersToCSV } from '../../../../utils/csvExport';
import TeamUserBqConsultantPerformance from './TeamUserBqConsultantPerformance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportIcon from '@mui/icons-material/Report';

interface IProps {
  rowCount: number;
  isPending: boolean;
  paginationModel: GridPaginationModel;
  setPaginationModel: (paginationModel: GridPaginationModel) => void;
  users: User[];
  onTeamManagerCheckboxChange: (row: User) => void;
  teams: Team[];
  onTeamChange: (row: User, teamId: string) => void;
  onRemoveMemberFromTeam: (row: User, teamId: string) => void;
  onCanReceiveLeadsCheckboxChange: (row: User) => void;
  onCanReceiveLeadsQuintoCheckboxChange: (row: User) => void;
  onCanReceiveLeadsPrestitoCheckboxChange: (row: User) => void;
}

const TeamsUsersTable = ({
  rowCount,
  isPending,
  paginationModel,
  setPaginationModel,
  users,
  onTeamManagerCheckboxChange,
  teams,
  onTeamChange,
  onRemoveMemberFromTeam,
  onCanReceiveLeadsCheckboxChange,
  onCanReceiveLeadsQuintoCheckboxChange,
  onCanReceiveLeadsPrestitoCheckboxChange,
}: IProps) => {
  const { formatDate } = useDateFormat();
  const [editUserSpecificCap, setEditUserSpecificCap] = useState<{
    capType: null | 'Ufficio' | '2' | '3' | '4' | '5';
    user: null | User;
  }>({ capType: null, user: null });

  const [editUserTelephone, setEditUserTelephone] = useState<User | null>(null);

  const apiRef = useGridApiRef();

  const handleExportCSV = () => {
    if (apiRef.current) {
      // Ottieni gli ID delle righe filtrate e ordinate
      const filteredRowIds = gridFilteredSortedRowIdsSelector(
        apiRef.current.state
      );

      // Mappa gli ID alle righe effettive
      const filteredUsers = filteredRowIds
        .map((id) => users.find((user) => user.id === id))
        .filter(Boolean) as User[];

      // Esporta solo i dati filtrati
      exportUsersToCSV(filteredUsers, teams);
    } else {
      // Fallback: esporta tutti gli utenti se l'API non è disponibile
      exportUsersToCSV(users, teams);
    }
  };

  // Componente personalizzato per il footer con select pagina
  const CustomFooter = () => {
    const apiRef = useGridApiContext();
    const paginationModel = useGridSelector(
      apiRef,
      gridPaginationModelSelector
    );

    // Calcola il numero totale di pagine
    const totalRows = users?.length || 0;
    const totalPages = Math.ceil(totalRows / paginationModel.pageSize);
    const currentPage = paginationModel.page + 1; // DataGrid usa 0-based indexing

    // Genera le opzioni per il select delle pagine
    const pageOptions = Array.from({ length: totalPages }, (_, i) => i + 1);

    // Handler per il cambio pagina tramite select
    const handlePageSelectChange = (selectedPage: number) => {
      apiRef.current.setPage(selectedPage - 1); // Converti da 1-based a 0-based
    };

    return (
      <GridFooterContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            sx={{ mr: 2 }}
          >
            Esporta CSV
          </Button>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <GridPagination />
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ ml: 2, mr: 4 }}
          >
            <Typography variant="body2" color="text.secondary">
              Pagina
            </Typography>
            <FormControl size="small">
              <Select
                value={currentPage}
                onChange={(e) => handlePageSelectChange(Number(e.target.value))}
                variant="standard"
                disableUnderline
                sx={{
                  '& .MuiSelect-select': {
                    paddingTop: 0,
                    paddingBottom: 0,
                  },
                  '&:before': {
                    display: 'none',
                  },
                  '&:after': {
                    display: 'none',
                  },
                }}
              >
                {pageOptions.map((page) => (
                  <MenuItem key={page} value={page}>
                    {page}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              di {totalPages}
            </Typography>
          </Stack>
        </Box>
      </GridFooterContainer>
    );
  };

  const teamsSelectOptions = [
    // { label: 'Seleziona un team', value: '' },
    ...teams.map((team) => ({
      value: team.id,
      label: `Team di ${team.teamManager.name}`,
    })),
  ];

  const tableColumns: GridColDef<User>[] = [
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1,
    },
    {
      field: 'telephoneNumber',
      headerName: 'Telefono',
      flex: 1.5,
      minWidth: 150,
      renderCell: ({ row }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

        const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
          setAnchorEl(event.currentTarget);
        };

        const handleMenuClose = () => {
          setAnchorEl(null);
        };

        const handleEditTelephone = () => {
          handleMenuClose();
          setEditUserTelephone(row);
        };

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
            }}
          >
            <Typography variant="body2" sx={{ flex: 1 }}>
              {row.telephoneNumber || 'N/A'}
            </Typography>

            <Box>
              <IconButton size="small" onClick={handleMenuClick}>
                <MoreVertIcon fontSize="small" />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleEditTelephone}>
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Modifica Telefono</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'caps',
      headerName: 'CAP',
      flex: 2,
      sortable: false,
      // Aggiungi valueGetter per permettere il filtraggio su testo
      valueGetter: ({ row }) => {
        const caps = [
          {
            type: 'capufficio',
            value: row.profileCollaboratori?.CapUfficio,
          },
          {
            type: 'cap2',
            value: row.profileCollaboratori?.Cap2,
          },
          {
            type: 'cap3',
            value: row.profileCollaboratori?.Cap3,
          },
          {
            type: 'cap4',
            value: row.profileCollaboratori?.Cap4,
          },
          {
            type: 'cap5',
            value: row.profileCollaboratori?.Cap5,
          },
        ];

        // Filtra solo i CAP popolati
        const populatedCaps = caps.filter((cap) => cap.value);

        // Se non ci sono CAP, restituisci "Nessun CAP" per permettere il filtraggio
        if (populatedCaps.length === 0) {
          return 'Nessun CAP';
        }

        // Crea una stringa che include sia i tipi che i valori per la ricerca
        const searchString = populatedCaps
          .map((cap) => `${cap.type} ${cap.value}`)
          .join(' ');

        return searchString;
      },
      renderCell: ({ row }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

        const caps = [
          {
            key: 'Ufficio',
            label: 'CAP Ufficio',
            value: row.profileCollaboratori?.CapUfficio,
            isPrimary: true,
          },
          {
            key: '2',
            label: 'CAP 2',
            value: row.profileCollaboratori?.Cap2,
            isPrimary: false,
          },
          {
            key: '3',
            label: 'CAP 3',
            value: row.profileCollaboratori?.Cap3,
            isPrimary: false,
          },
          {
            key: '4',
            label: 'CAP 4',
            value: row.profileCollaboratori?.Cap4,
            isPrimary: false,
          },
          {
            key: '5',
            label: 'CAP 5',
            value: row.profileCollaboratori?.Cap5,
            isPrimary: false,
          },
        ];

        const populatedCaps = caps.filter((cap) => cap.value); // Mostra solo i CAP popolati

        const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
          setAnchorEl(event.currentTarget);
        };

        const handleMenuClose = () => {
          setAnchorEl(null);
        };

        const handleEditSpecificCap = (capType: string) => {
          handleMenuClose();
          /* if (capType === 'CapUfficio') {
            setUserToEdit(row);
            return;
          }*/
          setEditUserSpecificCap({
            capType: capType as 'Ufficio' | '2' | '3' | '4' | '5',
            user: row,
          });
        };

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              width: '100%',
              flexWrap: 'wrap',
            }}
          >
            {/* Mostra i CAP come chip */}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
              {populatedCaps.length === 0 ? (
                <Chip
                  label="Nessun CAP"
                  size="small"
                  variant="outlined"
                  color="default"
                />
              ) : (
                populatedCaps.map((cap) => (
                  <Tooltip key={cap.key} title={`${cap.label}: ${cap.value}`}>
                    <Chip
                      label={cap.value}
                      size="small"
                      color={cap.isPrimary ? 'primary' : 'default'}
                      variant={cap.isPrimary ? 'filled' : 'outlined'}
                    />
                  </Tooltip>
                ))
              )}
            </Box>

            {/* Menu per le azioni */}
            <Box>
              <IconButton size="small" onClick={handleMenuClick}>
                <MoreVertIcon fontSize="small" />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {caps.map((cap) => (
                  <MenuItem
                    key={cap.key}
                    onClick={() => handleEditSpecificCap(cap.key)}
                  >
                    <ListItemIcon>
                      <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Modifica {cap.label}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'region',
      headerName: 'Regione',
      flex: 1,
      valueGetter: ({ row }) => row.profileCollaboratori?.Regione,
    },
    {
      field: 'province',
      headerName: 'Provincia',
      flex: 1,
      valueGetter: ({ row }) => row.profileCollaboratori?.ProvinciaUfficio,
    },
    {
      field: 'createdAt',
      headerName: 'Data di creazione',
      flex: 1,
      type: 'date',
      valueGetter: ({ row }) => new Date(row.createdAt),
      valueFormatter: ({ value }) => formatDate(value, 'dd/MM/yyyy'),
    },
    {
      field: 'isTeamManager',
      headerName: 'Team Manager',
      flex: 1,
      headerAlign: 'center',
      maxWidth: 150,
      type: 'boolean',
      valueGetter: ({ row }) => !!row.team,
      renderCell: ({ row }) => {
        // se l'utente è un membro di un team, non può essere team manager --> checkbox disabilitato
        const teamId =
          teams.find((t) => t.members?.some((member) => member.id === row.id))
            ?.id || '';
        const disabled = !!teamId;

        return (
          <Box display="flex" justifyContent="center" width="100%">
            <CheckboxInput
              disabled={disabled}
              checked={!!row.team}
              onChange={() => onTeamManagerCheckboxChange(row)}
            />
          </Box>
        );
      },
    },
    {
      field: 'team',
      sortable: false,
      headerName: 'Team',
      flex: 1,
      headerAlign: 'center',
      valueGetter: ({ row }) => {
        const teamId =
          row.team?.id ||
          teams.find((t) => t.members?.some((member) => member.id === row.id))
            ?.id ||
          undefined;

        if (teamId) {
          return teamsSelectOptions.find((t) => t.value === teamId)?.label;
        }
        return '';
      },
      renderCell: ({ row }) => {
        const teamId =
          row.team?.id ||
          teams.find((t) => t.members?.some((member) => member.id === row.id))
            ?.id ||
          undefined;

        if (teamId) {
          return (
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <Box width={42}>
                {!row.team && (
                  <IconButton
                    onClick={() => onRemoveMemberFromTeam(row, teamId)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
              <p>{teamsSelectOptions.find((t) => t.value === teamId)?.label}</p>
            </Box>
          );
        }

        if (teamsSelectOptions.length === 0) {
          return <p>Nessun team disponibile</p>;
        }

        return (
          <AutocompleteSelectInput
            selectOptions={teamsSelectOptions}
            value={teamId}
            /* eslint-disable-next-line */
            // @ts-expect-error
            onChange={(
              event: React.SyntheticEvent,
              /* eslint-disable-next-line */
              value: any
            ) => {
              onTeamChange(row, value?.value || '');
            }}
          />
        );
      },
    },
    {
      field: 'canReceiveLeads',
      sortable: true,
      headerName: 'Assegna Lead',
      flex: 1,
      headerAlign: 'center',
      maxWidth: 150,
      type: 'boolean',
      valueGetter: ({ row }) => !!row?.canReceiveLeads,
      renderCell: ({ row }) => {
        return (
          <Box display="flex" justifyContent="center" width="100%">
            <CheckboxInput
              checked={row?.canReceiveLeads}
              onChange={() => onCanReceiveLeadsCheckboxChange(row)}
            />
          </Box>
        );
      },
    },
    {
      field: 'canReceiveLeadsQuinto',
      sortable: true,
      headerName: 'Lead Quinto',
      flex: 1,
      headerAlign: 'center',
      maxWidth: 130,
      type: 'boolean',
      valueGetter: ({ row }) => !!row?.canReceiveLeadsQuinto,
      renderCell: ({ row }) => {
        return (
          <Box display="flex" justifyContent="center" width="100%">
            <CheckboxInput
              checked={row?.canReceiveLeadsQuinto}
              onChange={() => onCanReceiveLeadsQuintoCheckboxChange(row)}
            />
          </Box>
        );
      },
    },
    {
      field: 'canReceiveLeadsPrestito',
      sortable: true,
      headerName: 'Lead Prestito',
      flex: 1,
      headerAlign: 'center',
      maxWidth: 140,
      type: 'boolean',
      valueGetter: ({ row }) => !!row?.canReceiveLeadsPrestito,
      renderCell: ({ row }) => {
        return (
          <Box display="flex" justifyContent="center" width="100%">
            <CheckboxInput
              checked={row?.canReceiveLeadsPrestito}
              onChange={() => onCanReceiveLeadsPrestitoCheckboxChange(row)}
            />
          </Box>
        );
      },
    },
    {
      field: 'consultantPerformance',
      sortable: true,
      headerName: 'Performance',
      flex: 1,
      headerAlign: 'center',
      maxWidth: 100,
      valueGetter: ({ row }) => row?.bqWeeklyPerformance?.rating || '-',
      renderCell: ({ row }) => <TeamUserBqConsultantPerformance user={row} />,
    },
    {
      field: 'integrityCheck',
      sortable: true,
      headerName: 'Bq Integrity',
      flex: 1,
      headerAlign: 'center',
      maxWidth: 100,
      type: 'boolean',
      valueGetter: ({ row }) => !!row?.bqIntegrityCheck?.isConsistent,
      renderCell: ({ row }) => <TeamUserBqIntegrityStatus user={row} />,
    },
    {
      field: 'gCalendarAuthorization',
      sortable: true,
      headerName: 'GCalendar',
      flex: 1,
      headerAlign: 'center',
      maxWidth: 100,
      type: 'boolean',
      valueGetter: ({ row }) => !!row?.googleCalendar?.authorized,
      renderCell: ({ row }) => (
        <Tooltip
          title={
            row?.googleCalendar?.authorized
              ? 'Google Calendar connesso'
              : 'Google Calendar non connesso'
          }
        >
          {row?.googleCalendar?.authorized ? (
            <CheckCircleIcon color="success" />
          ) : (
            <ReportIcon color="error" />
          )}
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Paper>
        <DataGrid
          apiRef={apiRef}
          columns={tableColumns}
          rows={users || []}
          disableRowSelectionOnClick
          autoHeight
          getRowHeight={() => 'auto'}
          loading={isPending}
          pageSizeOptions={[10, 20, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 20 },
            },
          }}
          slots={{
            footer: CustomFooter,
          }}
          slotProps={{
            pagination: {
              showFirstButton: true,
              showLastButton: true,
            },
          }}
          localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
        />
      </Paper>
      {!!editUserSpecificCap.capType && !!editUserSpecificCap.user && (
        <TeamMemberEditSpecificCapModal
          user={editUserSpecificCap.user}
          capType={editUserSpecificCap.capType}
          handleClose={() =>
            setEditUserSpecificCap({ capType: null, user: null })
          }
        />
      )}
      {editUserTelephone && (
        <TeamMemberEditTelephoneModal
          user={editUserTelephone}
          handleClose={() => setEditUserTelephone(null)}
        />
      )}
    </div>
  );
};

export default TeamsUsersTable;
